import React, { memo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IdleTimer from 'react-idle-timer';
import SessionTimeoutModal from './SessionTimeoutModal';

let countdownInterval;
let timeout;
let disableContinue = false;

const IDLE_DELAY = 1000;
const IDLE_INTERVAL = 1000;
const IDLE_TIMER_DEBOUNCE_INTERVAL = 250;

const clearSessionTimeout = () => {
    clearTimeout(timeout);
};
const clearSessionInterval = () => {
    clearInterval(countdownInterval);
};
const isAuthenticated = () => sessionStorage.getItem('isAuthenticated') === 'true';

export const clearSessionData = all => {
    // clear all auth and poi related session data from session storage.
    // NOTE: App context may choose to keep some session state like Submission result to continue to
    // show confirmation page, so this is NOT wiped here
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('poiUserData');
    sessionStorage.removeItem('poiReferenceNumber');
    sessionStorage.removeItem('userLoginTimeStamp');
    all && sessionStorage.removeItem('userInfo');
};

export const SessionTimeout = ({ timeoutIntervalInSeconds, numberOfIntervals }) => {
    const [timeoutModalOpen, setTimeoutModalOpen] = useState(false);
    const [timeoutCountdown, setTimeoutCountdown] = useState(0);
    const idleTimer = useRef(null);
    const masterTimeoutConst = timeoutIntervalInSeconds * numberOfIntervals;
    const extraSeconds = 5;

    const handleLogout = () => {
        try {
            setTimeoutModalOpen(false);
            clearSessionInterval();
            clearSessionTimeout();
            handleClick();
        } catch (err) {
            console.error(err);
        }
    };

    const handleContinue = () => {
        setTimeoutModalOpen(false);
        clearSessionInterval();
        clearSessionTimeout();
    };

    const onActive = () => {
        if (!timeoutModalOpen) {
            clearSessionInterval();
            clearSessionTimeout();
        }
    };

    const handleClick = () => {
        if (isAuthenticated()) {
            clearSessionData(true);
            // we don't clear this in the basic clear
            sessionStorage.setItem('isAuthenticated', false);
            window.location.href = `${process.env.PUBLIC_URL}/logout`;
        }
    };

    const calculateCountDown = () => {
        const userSessionTimeStampVar = sessionStorage.getItem('userLoginTimeStamp');
        const userLoginTimeStamp = userSessionTimeStampVar ? Date.parse(userSessionTimeStampVar) : null;
        const currentTimeStamp = new Date();

        if (!userLoginTimeStamp) {
            disableContinue = true;
            return timeoutIntervalInSeconds;
        }

        const currentSessionDuration = Math.floor((currentTimeStamp.getTime() - userLoginTimeStamp) / 1000);

        if (currentSessionDuration > masterTimeoutConst) {
            disableContinue = true;
            return extraSeconds;
        }

        return Math.min(currentSessionDuration, timeoutIntervalInSeconds);
    };

    const onIdle = () => {
        if (isAuthenticated() && !timeoutModalOpen) {
            timeout = setTimeout(() => {
                let countDown = calculateCountDown();
                setTimeoutModalOpen(true);
                setTimeoutCountdown(countDown);
                countdownInterval = setInterval(() => {
                    if (countDown) {
                        setTimeoutCountdown(--countDown);
                    } else {
                        handleLogout(true);
                    }
                }, IDLE_INTERVAL);
            }, IDLE_DELAY);
        }
    };
    return (
        <>
            <IdleTimer
                ref={ idleTimer }
                onActive={ onActive }
                onIdle={ onIdle }
                debounce={ IDLE_TIMER_DEBOUNCE_INTERVAL }
                timeout={ timeoutIntervalInSeconds * 1000 }
            />
            <SessionTimeoutModal
                countdown={ timeoutCountdown }
                onContinue={ handleContinue }
                onLogout={ () => handleLogout(false) }
                open={ timeoutModalOpen }
                disableContinue={ disableContinue }
                data-testid='SessionTimeoutModal'
            />
        </>
    );
};

SessionTimeout.displayName = 'SessionTimeout';

SessionTimeout.propTypes = {
    timeoutIntervalInSeconds: PropTypes.number.isRequired,
    numberOfIntervals: PropTypes.number.isRequired
};

export default memo(SessionTimeout);
